import type { Property } from "@shared/schema";
import { apiRequest, parseErrorStatus, parseErrorCode, parseErrorId, isImpersonationCancelled } from "@/lib/queryClient";

export class UnconfirmedPropertyGroupSave extends Error {
  constructor(errorId?: string | null) {
    super("Couldn't confirm the group save. Refresh this page to check the saved group before making another change."
      + (errorId ? `\n\nError ID: ${errorId}` : ""));
  }
}

async function readProperty(url: string, signal: AbortSignal): Promise<Property> {
  const response = await apiRequest("GET", url, undefined, { signal });
  return response.json();
}

function confirmed(data: Property, id: number, group: string): boolean {
  return data?.id === id && (data.propertyGroup || "") === group;
}

/** Never replay an uncertain write: a dropped response can follow a commit. */
export async function savePropertyGroup(id: number, group: string): Promise<Property> {
  const url = `/api/app/properties/${id}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await apiRequest("PATCH", url, { propertyGroup: group }, { signal: controller.signal });
    const data: Property = await response.json();
    if (!confirmed(data, id, group)) throw new UnconfirmedPropertyGroupSave();
    return data;
  } catch (error) {
    const status = parseErrorStatus(error);
    if (isImpersonationCancelled(error) || (status !== null && status < 500)
      || (status === 503 && parseErrorCode(error) === "PROPERTY_GROUP_WRITE_ROLLED_BACK")) throw error;
    // Fresh, bounded read reconciles response loss and 5xx-after-commit. No
    // mutation retry, no stale query cache, and normal auth handling stays intact.
    const readController = new AbortController();
    const readTimer = setTimeout(() => readController.abort(), 4000);
    try {
      const data = await readProperty(url, readController.signal);
      if (confirmed(data, id, group)) return data;
    } catch (readError) {
      const readStatus = parseErrorStatus(readError);
      if (readStatus !== null && readStatus < 500) throw readError;
    } finally {
      clearTimeout(readTimer);
    }
    throw new UnconfirmedPropertyGroupSave(parseErrorId(error));
  } finally {
    clearTimeout(timer);
  }
}
